import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({ operations: false });
  const [hoveredItem, setHoveredItem] = useState(null);

  const gradientStyle = i18n.language === 'ar'
    ? 'linear-gradient(270deg, #000000 0%, #facc0c 100%)'
    : 'linear-gradient(90deg, #000000 0%, #facc0c 100%)';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { nameKey: 'navigation.dashboard', path: '/dashboard', roles: ['Admin', 'Purchasing', 'Inventory', 'Sales', 'Measurements', 'Accounting'], icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { nameKey: 'navigation.users', path: '/users', roles: ['Admin'], icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { nameKey: 'navigation.suppliers', path: '/suppliers', roles: ['Admin', 'Purchasing'], icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { nameKey: 'navigation.inventory', path: '/inventory', roles: ['Admin', 'Inventory', 'Purchasing'], icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
    { nameKey: 'navigation.categories', path: '/categories', roles: ['Admin', 'Inventory', 'Purchasing'], icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
    { nameKey: 'navigation.purchases', path: '/purchases', roles: ['Admin', 'Purchasing'], icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' },
    { nameKey: 'navigation.customers', path: '/customers', roles: ['Admin', 'Sales'], icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { nameKey: 'navigation.salesOrders', path: '/sales-orders', roles: ['Admin', 'Sales'], icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
    { nameKey: 'navigation.measurements', path: '/measurements', roles: ['Admin', 'Measurements', 'Sales'], icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01' },
    { nameKey: 'navigation.deliveries', path: '/deliveries', roles: ['Admin', 'Sales'], icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
    { nameKey: 'navigation.workers', path: '/workers', roles: ['Admin', 'Lab'], icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    {
      nameKey: 'navigation.lab',
      groupId: 'lab',
      roles: ['Admin', 'Lab'],
      icon: '🧪',
      children: [
        { nameKey: 'lab.title', path: '/lab', roles: ['Admin', 'Lab'], icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { nameKey: 'lab.dashboard', path: '/lab/dashboard', roles: ['Admin', 'Lab'], icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        { nameKey: 'navigation.fabricSewingTypes', path: '/fabric-sewing-types', roles: ['Admin', 'Lab'], icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
      ]
    },
    {
      nameKey: 'navigation.operations',
      groupId: 'operations',
      roles: ['Admin', 'Sales'],
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      children: [
        { nameKey: 'navigation.fees', path: '/fees', roles: ['Admin', 'Sales'], icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { nameKey: 'navigation.vat', path: '/vat', roles: ['Admin', 'Sales'], icon: 'M9 7h6m0 10v-3m-3 3v-5m-4 5V7m16 0a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7z' },
        { nameKey: 'navigation.discounts', path: '/discounts', roles: ['Admin', 'Sales'], icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
      ]
    },
    {
      nameKey: 'navigation.accounting',
      groupId: 'accounting',
      roles: ['Admin', 'Accounting'],
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      children: [
        { nameKey: 'navigation.accountingDashboard', path: '/accounting', roles: ['Admin', 'Accounting'], icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2z' },
        { nameKey: 'navigation.salesReports', path: '/accounting/sales-report', roles: ['Admin', 'Accounting'], icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { nameKey: 'navigation.vatReports', path: '/accounting/vat-report', roles: ['Admin', 'Accounting'], icon: 'M9 7h6m0 10v-3m-3 3v-5m-4 5V7m16 0a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7z' },
        { nameKey: 'navigation.paymentTracking', path: '/accounting/payment-tracking', roles: ['Admin', 'Accounting'], icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { nameKey: 'navigation.financialSummary', path: '/accounting/financial-summary', roles: ['Admin', 'Accounting'], icon: 'M7 12a1 1 0 110-2 1 1 0 010 2zM15 12a1 1 0 110-2 1 1 0 010 2z' },
        { nameKey: 'navigation.returnsReport', path: '/accounting/returns-report', roles: ['Admin', 'Accounting'], icon: 'M9 11l3 3L20 4m0 0a9 9 0 11-18 0 9 9 0 0118 0z' },
      ]
    },
    { nameKey: 'navigation.returns', path: '/returns', roles: ['Admin', 'Sales'], icon: 'M9 11l3 3L20 4m0 0a9 9 0 11-18 0 9 9 0 0118 0z' },
    { nameKey: 'navigation.auditLogs', path: '/audit-logs', roles: ['Admin', 'Accounting'], icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { nameKey: 'navigation.settings', path: '/settings', roles: ['Admin', 'Lab'], icon: 'M12 8v8m8-8H4' },
    // { nameKey: 'navigation.invoicePrinterSettings', path: '/invoice-printer-settings', roles: ['Admin', 'Sales'], icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ];

  const isActivePath = (path) => location.pathname === path;

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupActive = (group) => {
    return group.children?.some(child => isActivePath(child.path));
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        style={{
          background: 'linear-gradient(135deg, #000000, #1a1a1a)',
        }}
        className={`${sidebarOpen ? 'w-64' : 'w-20'} text-white transition-all duration-300 ease-in-out shadow-2xl flex flex-col h-screen`}>
        <div className="flex items-center justify-between p-4 flex-shrink-0" style={{ borderBottom: '1px solid #5a5a5c' }}>
          {sidebarOpen && (
            <div className="flex items-center space-x-3">
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{ background: gradientStyle }}>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <span className="font-bold text-lg">Fabric MFG</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg transition-colors duration-200"
            style={{
              '&:hover': { backgroundColor: '#5a5a5c' }
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#5a5a5c'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
            </svg>
          </button>
        </div>

        <nav className="mt-6 px-3 space-y-1 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            if (!hasAnyRole(item.roles)) return null;

            // Group item with children
            if (item.children) {
              const isExpanded = expandedGroups[item.groupId];
              const groupActive = isGroupActive(item);

              return (
                <div key={item.groupId}>
                  <button
                    onClick={() => toggleGroup(item.groupId)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200"
                    style={{
                      background: groupActive ? gradientStyle : (hoveredItem === `group-${item.groupId}` ? '#5a5a5c' : 'transparent'),
                      boxShadow: groupActive ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseEnter={() => {
                      if (!groupActive) setHoveredItem(`group-${item.groupId}`);
                    }}
                    onMouseLeave={() => {
                      setHoveredItem(null);
                    }}
                  >
                    <div className="flex items-center">
                      <svg className="h-6 w-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                      {sidebarOpen && (
                        <span className="ltr:ml-3 rtl:mr-3 font-medium">{t(item.nameKey)}</span>
                      )}
                    </div>
                    {sidebarOpen && (
                      <svg
                        className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {/* Children */}
                  {isExpanded && sidebarOpen && (
                    <div className="ltr:ml-6 rtl:mr-6 mt-1 space-y-1">
                      {item.children.map((child) =>
                        hasAnyRole(child.roles) ? (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="flex items-center px-4 py-2 rounded-lg transition-all duration-200 text-sm"
                            style={{
                              backgroundColor: isActivePath(child.path) ? '#facc0c' : (hoveredItem === `child-${child.path}` ? '#5a5a5c' : 'transparent'),
                              color: isActivePath(child.path) ? '#2d2d2d' : 'white',
                              boxShadow: isActivePath(child.path) ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                            }}
                            onMouseEnter={() => {
                              if (!isActivePath(child.path)) setHoveredItem(`child-${child.path}`);
                            }}
                            onMouseLeave={() => {
                              setHoveredItem(null);
                            }}
                          >
                            <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={child.icon} />
                            </svg>
                            <span className="ltr:ml-3 rtl:mr-3 font-medium">{t(child.nameKey)}</span>
                          </Link>
                        ) : null
                      )}
                    </div>
                  )}
                </div>
              );
            }

            // Regular menu item
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center px-4 py-3 rounded-lg transition-all duration-200"
                style={{
                  background: isActivePath(item.path) ? gradientStyle : (hoveredItem === `menu-${item.path}` ? '#5a5a5c' : 'transparent'),
                  boxShadow: isActivePath(item.path) ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onMouseEnter={() => {
                  if (!isActivePath(item.path)) setHoveredItem(`menu-${item.path}`);
                }}
                onMouseLeave={() => {
                  setHoveredItem(null);
                }}
              >
                <svg className="h-6 w-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {sidebarOpen && (
                  <span className="ltr:ml-3 rtl:mr-3 font-medium">{t(item.nameKey)}</span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-md border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
               Sales Management System
              </h1>
              <p className="text-sm text-gray-600 mt-1">Manage your operations efficiently</p>
            </div>

            <div className="flex items-center space-x-4">
              <LanguageSwitcher />

              <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg">
                <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  {user?.full_name?.charAt(0)}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-600">{t(`users.roles.${user?.role}`)}</p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
